import type { SxProps, Theme } from "@mui/material";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import type { ReactNode } from "react";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { InHouseLineChart } from "../../../components/UIToolkit/Graphs/LineChart/InHouseLineChart";
import type { InHouseLineDataItem } from "../../../components/UIToolkit/Graphs/LineChart/types";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useRoleColors } from "../../../hooks/useRoleColors";
import { ALPHA, INK_BLACK, NEUTRAL, SURFACE } from "../../../theme/tokens";
import { NurseFullRole } from "../../../utils/models";

type Role = "RN" | "CNA" | "LPN";

type StaffMemberSatisfaction = {
	name: string;
	role: Role;
	rating: number;
};

type DailyStaffData = {
	[role in Role]: StaffMemberSatisfaction[];
};

/**
 * A simple PRNG function to generate consistent "random" numbers based on a seed.
 * @param seed - The seed value to initialize the PRNG.
 * @returns A function that generates a pseudo-random number between 0 and 1 each time it's called.
 */
function mulberry32(seed: number) {
	let t = seed >>> 0;
	return () => {
		t += 0x6d2b79f5;
		let r = Math.imul(t ^ (t >>> 15), 1 | t);
		r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
		return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
	};
}

/**
 * Generate individual staff satisfaction data for a given role and target average
 */
const generateStaffMembers = (
	role: Role,
	targetAvg: number,
	count: number,
	rand: () => number,
	nameOffset: number,
	staffNames: string[],
): StaffMemberSatisfaction[] => {
	const staff: StaffMemberSatisfaction[] = [];
	let sum = 0;

	// Generate count-1 random ratings, then calculate the last one to hit target average
	for (let i = 0; i < count - 1; i++) {
		// Generate ratings with some variance around the target
		const variance = 1.5;
		const rating = Math.max(1, Math.min(5, targetAvg + (rand() * variance * 2 - variance)));
		const roundedRating = Math.round(rating * 10) / 10;
		sum += roundedRating;

		staff.push({
			name: staffNames[(nameOffset + i) % staffNames.length],
			role,
			rating: roundedRating,
		});
	}

	// Calculate the last rating to achieve target average
	const lastRating = Math.max(1, Math.min(5, targetAvg * count - sum));
	staff.push({
		name: staffNames[(nameOffset + count - 1) % staffNames.length],
		role,
		rating: Math.round(lastRating * 10) / 10,
	});

	// Sort by rating (high to low)
	staff.sort((a, b) => b.rating - a.rating);

	return staff;
};

type RoleDataResult = {
	roleData: { [role in Role]: InHouseLineDataItem[] };
	dailyStaffData: Map<string, DailyStaffData>;
};

const getStaffSatisfactionDataByRole = (staffNames: string[], dateFrom: DateTime, dateTo?: DateTime, seed = 42): RoleDataResult => {
	/**
	 * Demo data generation logic:
	 * - For RN and LPN: maintain 3-5 average throughout the entire period
	 * - For CNA: start at 3-5 average, then drop to 1-3 in the last period
	 */
	const rand = mulberry32(seed);

	const startDate = dateFrom.startOf("day");
	const endDate = dateTo ? dateTo.endOf("day") : DateTime.now().endOf("day");
	const dateSatisfactionBarrier =
		endDate.diff(startDate, "days").days >= 30
			? endDate.minus({ week: 2 })
			: endDate.diff(startDate, "days").days >= 60
				? endDate.minus({ month: 1 })
				: startDate;

	const roleData: { [role in Role]: InHouseLineDataItem[] } = {
		RN: [],
		CNA: [],
		LPN: [],
	};

	const dailyStaffData = new Map<string, DailyStaffData>();

	let currentDate = startDate;
	let dayIndex = 0;

	while (currentDate <= endDate) {
		const isLowSatisfactionPeriod = currentDate >= dateSatisfactionBarrier;
		const dateKey = currentDate.toISODate();

		const dayStaffData: DailyStaffData = {
			RN: [],
			CNA: [],
			LPN: [],
		};

		// Generate data for each role
		const roles: Role[] = ["RN", "CNA", "LPN"];
		const roleScores: { [role in Role]: number } = { RN: 0, CNA: 0, LPN: 0 };

		roles.forEach((role) => {
			// CNA gets the drop, others stay at 3-5
			const shouldDrop = role === "CNA" && isLowSatisfactionPeriod;

			const satisfactionScore = shouldDrop
				? Math.floor(rand() * 21 + 10) / 10 // 1.0 - 3.0
				: Math.floor(rand() * 21 + 30) / 10; // 3.0 - 5.0

			roleData[role].push({
				date: currentDate,
				value: satisfactionScore,
			});

			roleScores[role] = satisfactionScore;
		});

		// Generate 4-6 total staff members across all roles
		const totalStaffCount = Math.floor(rand() * 3) + 4; // 4-6 total

		// Distribute staff across roles (ensure each role gets at least 1)
		const staffPerRole: { [role in Role]: number } = { RN: 1, CNA: 1, LPN: 1 };
		let remainingStaff = totalStaffCount - 3; // Already assigned 1 to each

		// Randomly distribute remaining staff
		while (remainingStaff > 0) {
			const randomRole = roles[Math.floor(rand() * 3)];
			staffPerRole[randomRole]++;
			remainingStaff--;
		}

		// Generate staff for each role
		let nameOffset = dayIndex * 10;
		roles.forEach((role) => {
			dayStaffData[role] = generateStaffMembers(role, roleScores[role], staffPerRole[role], rand, nameOffset, staffNames);
			nameOffset += staffPerRole[role];
		});

		if (dateKey) {
			dailyStaffData.set(dateKey, dayStaffData);
		}

		currentDate = currentDate.plus({ days: 1 });
		dayIndex++;
	}

	return { roleData, dailyStaffData };
};

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, dailyStaffData, customTooltipYOffset = 0 }: any) => {
	const roleColor = useRoleColors();
	if (!active || !payload || payload.length === 0) return null;

	// Get the date from the payload
	const dateMs = payload[0]?.payload?.date;
	if (!dateMs) return null;

	const date = DateTime.fromMillis(dateMs);
	const dateKey = date.toISODate();

	if (!dateKey) return null;

	const staffData = dailyStaffData.get(dateKey);
	if (!staffData) return null;

	// Combine all staff from all roles and sort by rating (high to low)
	const allStaff: StaffMemberSatisfaction[] = [...staffData.RN, ...staffData.CNA, ...staffData.LPN];
	allStaff.sort((a, b) => b.rating - a.rating);

	// Display all staff members (they're already limited to 1-3 per role in generation)

	return (
		<Box
			sx={{
				backgroundColor: SURFACE,
				border: `1px solid ${NEUTRAL[400]}`,
				borderRadius: "4px",
				padding: "12px",
				boxShadow: `0 2px 8px ${alpha(INK_BLACK, 0.15)}`,
				minWidth: "280px",
				zIndex: 1000,
				position: "relative",
				marginTop: `${customTooltipYOffset}px`,
			}}
		>
			<Typography
				variant="body2"
				sx={{
					fontWeight: 700,
					mb: 1,
				}}
			>
				{date.toFormat("EEEE, MMMM d")}
			</Typography>
			{/* Role Averages */}
			<Box
				sx={{
					mb: 1.5,
				}}
			>
				{payload.map((entry: any) => (
					<Box
						key={entry.dataKey}
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 0.5,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									backgroundColor: entry.color,
								}}
							/>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 600,
								}}
							>
								{entry.name}
							</Typography>
						</Box>
						<Typography
							variant="caption"
							sx={{
								fontWeight: 700,
							}}
						>
							{typeof entry.value === "number" ? entry.value.toFixed(1) : entry.value}
						</Typography>
					</Box>
				))}
			</Box>
			{/* Divider */}
			<Box sx={{ borderTop: `1px solid ${NEUTRAL[300]}`, my: 1 }} />
			{/* Individual Staff Members */}
			<Typography
				variant="caption"
				sx={{
					fontWeight: 700,
					color: "text.secondary",
					mb: 0.5,
					display: "block",
				}}
			>
				Staff Responses
			</Typography>
			{allStaff.map((staff, idx) => (
				<Box
					key={`${staff.name}-${idx}`}
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 0.5,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<Typography variant="caption" sx={{ minWidth: "32px", color: roleColor(NurseFullRole[staff.role]).ink, fontWeight: 600 }}>
							{staff.role}
						</Typography>
						<Typography variant="caption">{staff.name}</Typography>
					</Box>
					<Typography
						variant="caption"
						color={staff.rating >= 4 ? "success.main" : staff.rating >= 3 ? "warning.main" : "error.main"}
						sx={{
							fontWeight: 600,
						}}
					>
						{staff.rating.toFixed(1)}
					</Typography>
				</Box>
			))}
		</Box>
	);
};

export const StaffSatisfaction = ({
	dateFrom,
	dateTo,
	trailing,
	containerSx,
	onClick,
}: {
	dateFrom: DateTime;
	dateTo?: DateTime;
	trailing?: ReactNode;
	containerSx?: SxProps<Theme>;
	onClick?: () => void;
}) => {
	const roleColor = useRoleColors();
	const {
		hospitalData: { nurses },
		getNurseFormattedName,
	} = useHospitalData();
	const staffNames = nurses.map((nurse) => getNurseFormattedName(nurse.id));
	const { roleData, dailyStaffData } = getStaffSatisfactionDataByRole(staffNames, dateFrom, dateTo);

	return (
		<CommonDashboardBox
			onClick={onClick}
			sx={
				((theme) => ({
					display: "flex",
					flexDirection: "column",
					alignItems: "start",
					gap: 1,
					width: "calc(100% - 4px)",
					...containerSx,
					...(onClick && {
						cursor: "pointer",
						"&:hover": { boxShadow: `0px 2px 12px 0px ${alpha(theme.palette.primary.main, ALPHA.FOCUS_RING)}` },
					}),
				})) as SxProps<Theme>
			}
		>
			<CommonDashboardHeader label={"Staff Satisfaction"} trailing={trailing} />
			<InHouseLineChart
				width={"100%"}
				height={containerSx ? "100%" : undefined}
				margin={{
					top: 4,
					right: 20,
					bottom: 0,
					left: 0,
				}}
				leftAxisData={{
					dataGroups: [
						{
							name: "RN",
							color: roleColor(NurseFullRole.RN).dot,
							data: roleData.RN,
						},
						{
							name: "CNA",
							color: roleColor(NurseFullRole.CNA).dot,
							data: roleData.CNA,
						},
						{
							name: "LPN",
							color: roleColor(NurseFullRole.LPN).dot,
							data: roleData.LPN,
						},
					],
					minValue: 1,
					maxValue: 5,
				}}
				customTooltip={(props: any) => <CustomTooltip {...props} dailyStaffData={dailyStaffData} />}
				customTooltipYOffset={-300}
			/>
		</CommonDashboardBox>
	);
};
